/**
 * Renderer orchestration for S14 exports: definition JSON, REAL PNG/PDF
 * capture (rects measured from the live DOM, captured main-side), and the
 * import flow (read file → parse → repository import → conflict/remap
 * signals for the dialogs). Analytics fire ON SUCCESS only (repo rule).
 *
 * The S15 interactive HTML snapshot lives in its own
 * `htmlSnapshotExportService.ts` (reuses `collectCaptureState` below) —
 * kept separate so this file stays close to its pre-S15 size.
 */
import type { Dashboard } from '../types/dashboardTypes';
import type { BoundParamValue } from '../types/dashboardTypes';
import { useDashboardStore } from '../store/dashboardStore';
import { useDashboardParamStore } from '../store/dashboardParamStore';
import { useDashboardCrossFilterStore } from '../store/dashboardCrossFilterStore';
import { buildCaptureStateLine, groupTilesIntoRows } from './captureLayout';
import { collectConnectionRefs, parseDefinition, serializeDefinition, type ConnectionRef } from './definitionIo';

const trackExport = (format: 'json' | 'png' | 'pdf'): void => {
  window.electronAPI?.analytics?.track('feature:dashboard_export', { format }).catch(() => {});
};

const api = () => window.electronAPI?.dashboards;

export type ExportOutcome = { ok: boolean; error?: string; cancelled?: boolean };

const toOutcome = (
  result: { success: true; filePath: string } | { success: false; error: string; cancelled?: boolean } | undefined,
  format: 'json' | 'png' | 'pdf',
): ExportOutcome => {
  if (!result) return { ok: false, error: 'Export is not available in this build' };
  if (!result.success) return { ok: false, error: result.error, cancelled: result.cancelled };
  trackExport(format);
  return { ok: true };
};

export async function exportDashboardDefinition(dashboard: Dashboard): Promise<ExportOutcome> {
  const result = await api()?.exportDefinition({
    json: serializeDefinition(dashboard),
    suggestedFileName: `${dashboard.name}.json`,
  });
  return toOutcome(result, 'json');
}

const displayValue = (value: BoundParamValue): string => {
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (value && typeof value === 'object' && 'from' in value) return `${value.from} → ${value.to}`;
  return String(value);
};

/**
 * Active parameter values + active cross-filters, structured (label/display
 * pairs) — shared by the PDF capture header line (`captureStateLine`, joined
 * into one string) and the S15 HTML snapshot header strip (kept as chips).
 */
export function collectCaptureState(
  dashboard: Dashboard,
): { params: Array<{ label: string; display: string }>; filters: Array<{ field: string; display: string }> } {
  const values = useDashboardParamStore.getState().valuesByDashboard[dashboard.id] ?? {};
  const params = dashboard.parameters
    .filter((def) => values[def.id] !== undefined)
    .map((def) => ({
      // The time-range variant has no label field — its id reads fine ("range").
      label: 'label' in def ? def.label : def.id,
      display: displayValue(values[def.id] as BoundParamValue),
    }));
  const filters = (useDashboardCrossFilterStore.getState().byDashboard[dashboard.id] ?? []).map(
    (f) => ({ field: f.dashboardField, display: f.value === null ? 'IS NULL' : String(f.value) }),
  );
  return { params, filters };
}

/** Header line for captures: active parameter values + active cross-filters. */
export function captureStateLine(dashboard: Dashboard): string | undefined {
  const { params, filters } = collectCaptureState(dashboard);
  return buildCaptureStateLine(params, filters);
}

/** The app's current theme — reused by S15's HTML snapshot as the baked default. */
export const isDarkTheme = (): 'light' | 'dark' =>
  document.documentElement.classList.contains('dark') ? 'dark' : 'light';

const domRect = (selector: string): { x: number; y: number; width: number; height: number } | null => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.x, y: r.y, width: r.width, height: r.height };
};

export async function captureDashboardPng(dashboard: Dashboard): Promise<ExportOutcome> {
  await settleOverlays();
  const rect = domRect(`[data-dashboard-capture="${dashboard.id}"]`);
  if (!rect) return { ok: false, error: 'Dashboard is not visible' };
  const result = await api()?.capturePng({ rect, suggestedFileName: `${dashboard.name}.png` });
  return toOutcome(result, 'png');
}

const nextPaint = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

/**
 * Lets the kebab menu (or any overlay) finish its exit animation before a
 * capture. Capturing immediately after the item click both bakes the
 * half-closed menu into the image AND stalls the renderer mid-animation so
 * Radix never sees `animationend` — the menu then lingers as a zombie
 * overlay (observed in the S14 e2e).
 */
const settleOverlays = async () => {
  await new Promise((resolve) => setTimeout(resolve, 250));
  await nextPaint();
};

/**
 * Captures one tile: scrolls it into view first — `capturePage` only sees
 * the visible viewport, so tall dashboards (the case PDF export exists for)
 * would otherwise produce blank tiles below the fold.
 */
async function captureTileDataUrl(tileId: string): Promise<string | null> {
  const el = document.querySelector(`[data-tile-id="${CSS.escape(tileId)}"]`);
  if (!el) return null;
  el.scrollIntoView({ block: 'nearest' });
  await nextPaint();
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return null;
  const result = await api()?.captureTile({
    rect: { x: r.x, y: r.y, width: r.width, height: r.height },
  });
  return result?.success ? result.dataUrl : null;
}

export async function captureDashboardPdf(dashboard: Dashboard): Promise<ExportOutcome> {
  await settleOverlays();
  const rows: Array<Array<{ title: string; pngDataUrl: string; widthFraction: number }>> = [];
  for (const row of groupTilesIntoRows(dashboard.tiles)) {
    const tiles: Array<{ title: string; pngDataUrl: string; widthFraction: number }> = [];
    for (const tile of row) {
      const pngDataUrl = await captureTileDataUrl(tile.id);
      if (pngDataUrl) {
        tiles.push({ title: tile.title, pngDataUrl, widthFraction: tile.widthFraction });
      }
    }
    if (tiles.length > 0) rows.push(tiles);
  }
  if (rows.length === 0) return { ok: false, error: 'No visible tiles to capture' };
  const result = await api()?.capturePdf({
    title: dashboard.name,
    stateLine: captureStateLine(dashboard),
    theme: isDarkTheme(),
    rows,
    suggestedFileName: `${dashboard.name}.pdf`,
  });
  return toOutcome(result, 'pdf');
}

export type ImportFlowResult =
  | { kind: 'cancelled' }
  | { kind: 'error'; error: string }
  /** Imported (possibly under a new id); refs list connections the local machine doesn't have. */
  | { kind: 'imported'; dashboard: Dashboard; warning?: string; missingRefs: ConnectionRef[] }
  /** The definition's id already exists — caller offers "import as copy (new id)". */
  | { kind: 'conflict'; parsed: Dashboard };

export async function importDashboardDefinition(
  knownConnectionIds: ReadonlySet<string>,
  options?: { explicitPath?: string; json?: string },
): Promise<ImportFlowResult> {
  let json = options?.json;
  if (json === undefined) {
    const read = await api()?.readDefinitionFile(options?.explicitPath);
    if (!read) return { kind: 'error', error: 'Import is not available in this build' };
    if (!read.success) {
      return read.cancelled ? { kind: 'cancelled' } : { kind: 'error', error: read.error };
    }
    json = read.json;
  }
  const parsed = parseDefinition(json);
  if (!parsed.ok) return { kind: 'error', error: parsed.error };
  // Through the store (not the raw bridge) so the renderer mirror — switcher,
  // cap meter — picks the imported dashboard up immediately.
  const result = await useDashboardStore
    .getState()
    .importDefinition(serializeDefinition(parsed.dashboard));
  if (!result.ok) {
    if (result.error === 'conflict') return { kind: 'conflict', parsed: parsed.dashboard };
    return { kind: 'error', error: result.detail ?? result.error };
  }
  const missingRefs = collectConnectionRefs(result.value).filter(
    (ref) => !knownConnectionIds.has(ref.connectionId),
  );
  return {
    kind: 'imported',
    dashboard: result.value,
    warning: result.detail,
    missingRefs,
  };
}
